import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchDrierStatusReportEvenThunk,
  fetchDrierStatusReportOddThunk,
  fetchDrierStatusThunk,
  fetchDryingTransactionTempThunk,
} from "../../store/reports/thunk";
import AsyncStorage from "@react-native-async-storage/async-storage";
import "../../styles/global.css";
import noDataImage from "../../assets/no_data.png";
import {
  Home,
  PauseCircle,
  PlayCircle,
  ChevronLeftCircle,
  ChevronRightCircle,
} from "lucide-react";
import { setDate } from "../../store/reports/actions";
import { APP_CONSTANTS } from "../../utilities/app_constants";

const DrierStatusReport = ({
  reportData,
  onNext,
  onPrevious,
  onPause,
  isPaused,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [userId, setUserId] = useState(null);
  const [params, setParams] = useState({});

  useEffect(() => {
    const fetchUserId = async () => {
      const userId = await AsyncStorage.getItem("userId");
      setUserId(userId);
    };
    fetchUserId();
  }, []);

  const {
    drierStatusReport,
    drierStatusReportEven,
    drierStatusReportOdd,
    DryingTransactionTemp,
    currentDate,
    loading,
    error,
  } = useSelector((state) => state.report);

  useEffect(() => {
    if (reportData?.url) {
      const urlParams = new URLSearchParams(reportData.url.split("?")[1]);
      const extractedParams = {
        orgId: urlParams.get("AD_Org_ID"),
        season: urlParams.get("DGM_Season_ID"),
        drierType: urlParams.get("Typeofdryer"),
        warehouse: urlParams.get("warehouse"),
        note: urlParams.get("Note"),
      };
      setParams(extractedParams);
    }
  }, [reportData]);

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  useEffect(() => {
    if (params.orgId && params.season && params.drierType) {
      const date = getCurrentDate();
      dispatch(setDate(date));
      dispatch(
        fetchDrierStatusThunk(params.orgId, params.season, params.drierType),
      );
      dispatch(
        fetchDrierStatusReportEvenThunk(
          params.orgId,
          params.season,
          params.drierType,
        ),
      );
      dispatch(
        fetchDrierStatusReportOddThunk(
          params.orgId,
          params.season,
          params.drierType,
        ),
      );
      dispatch(fetchDryingTransactionTempThunk(params.orgId, params.season));
    }
  }, [dispatch, params]);

  useEffect(() => {
    if (!isPaused) {
      const intervalInSeconds = parseInt(reportData?.interval, 10);
      const displayTime =
        !isNaN(intervalInSeconds) && intervalInSeconds > 0
          ? intervalInSeconds * 1000
          : APP_CONSTANTS.REPORT_DISPLAY_TIME;

      const timeout = setTimeout(() => {
        onNext();
      }, displayTime);

      return () => clearTimeout(timeout);
    }
  }, [onNext, isPaused, reportData?.interval]);

  const calculateUsableBinCapacity = () => {
    const allData = [...(filteredData || []), ...(filteredDataOdd || [])];

    if (allData.length === 0) {
      return "-";
    }

    const emptyBins = allData.filter((item) => {
      const docStatus = item.documentstatus?.trim().toLowerCase();
      return docStatus === "empty";
    });

    if (emptyBins.length === 0) {
      return "-";
    }

    const capacityCountMap = new Map();

    emptyBins.forEach((item) => {
      const binCapacity = item.bincapacity || item.Bincapacity;

      if (binCapacity && binCapacity !== "-") {
        let initialNumber = binCapacity;

        if (typeof binCapacity === "string" && binCapacity.includes("-")) {
          initialNumber = parseFloat(binCapacity.split("-")[0]);
        } else if (
          typeof binCapacity === "string" &&
          !isNaN(parseFloat(binCapacity))
        ) {
          initialNumber = parseFloat(binCapacity);
        } else if (typeof binCapacity === "number") {
          initialNumber = binCapacity;
        }

        if (!isNaN(initialNumber) && initialNumber > 0) {
          const capacityKey = initialNumber.toString();
          const currentCount = (capacityCountMap.get(capacityKey) || 0) + 1;
          capacityCountMap.set(capacityKey, currentCount);
        }
      }
    });

    let totalSum = 0;

    for (const [capacity, count] of capacityCountMap.entries()) {
      totalSum += parseFloat(capacity) * count;
    }

    const usableCapacity = (totalSum * 85) / 100;
    return usableCapacity.toFixed(2);
  };

  const columnMappingTable2 = {
    bincapacity: "Bin Capacity (MT)",
    cycleno: "Cycle No",
    documentstatus: "Document Status",
    fieldvariety: "Field Variety",
    qaflag: "QA Flag",
    totallintakehandlingqty: "Total Intake Quantity",
    dryingstarttime: "Drying Start Time",
    avgintakemoisture: "Drying Avg Intake Moisture",
    airflowndatetime: "AirFlow Down Time",
    finalmoisture: "AirFlow Down Moisture",
    checkingdatetime: "Moisture Checking Time",
    moisture: "Moisture Checking Recent Moisture",
    dryingenddatetime: "Drying End Date & Time",
    totaltime: "Total Drying (HH:MM)",
    drydownratee: "Dry Down Rate",
  };

  const columnMappingTable3 = {
    bincapacity: "Bin Capacity (MT)",
    cycleno: "Cycle No",
    documentstatus: "Document Status",
    fieldvariety: "Field Variety",
    qaflag: "QA Flag",
    totallintakehandlingqty: "Total Intake Quantity",
    dryingstartdatetime: "Drying Start Time",
    avgintakemoisture: "Drying Avg Intake Moisture",
    airflowdowndatetime: "AirFlow Down Time",
    finalmoisture: "AirFlow Down Moisture",
    checkingdatetime: "Moisture Checking Time",
    moisture: "Moisture Checking Recent Moisture",
    dryingenddatetime: "Drying End Date & Time",
    totaltime: "Total Drying (HH:MM)",
    drydownratee: "Dry Down Rate",
  };

  const extractHoursFromTime = (timeValue) => {
    if (!timeValue || timeValue === "-") return 0;
    const timeStr = String(timeValue);
    let hours = 0;
    if (timeStr.includes(":")) {
      const [hoursStr] = timeStr.split(":");
      hours = parseFloat(hoursStr) || 0;
    } else {
      hours = parseFloat(timeStr) || 0;
    }
    return hours;
  };

  const calculateBinCapacityPercentage = (binCapacity, totalIntake) => {
    if (
      !binCapacity ||
      !totalIntake ||
      binCapacity === "-" ||
      totalIntake === "-"
    ) {
      return { display: "-", percentage: 0 };
    }
    try {
      const binCapacityInKgs = parseFloat(binCapacity) * 1000;
      const totalIntakeInKgs = parseFloat(totalIntake);
      if (binCapacityInKgs === 0) {
        return { display: `${binCapacity} - 0%`, percentage: 0 };
      }
      const percentage = Math.round(
        (totalIntakeInKgs / binCapacityInKgs) * 100,
      );
      return {
        display: `${binCapacity} - ${percentage}%`,
        percentage: percentage,
      };
    } catch (error) {
      return { display: binCapacity, percentage: 0 };
    }
  };

  const getColumnColorForBin = (binData) => {
    if (binData.documentstatus?.trim().toLowerCase() === "empty") {
      return "transparent";
    }
    const dryingHours = extractHoursFromTime(binData.totaltime);
    if (dryingHours > 120) {
      return "#FF7070";
    } else if (dryingHours > 100) {
      return "#FFC000";
    }
    if (binData.documentstatus === "Airflow UP") {
      return "#A9D18E";
    } else if (binData.documentstatus === "Airflow Down") {
      return "#FFC000";
    }
    return "transparent";
  };

  const getMaxCycleData = (data) => {
    const maxCycleMap = {};
    data.forEach((item) => {
      const bin = item.binname;
      const cycle = Number(item.cycleno) || 0;
      if (!maxCycleMap[bin] || cycle > maxCycleMap[bin]) {
        maxCycleMap[bin] = cycle;
      }
    });
    return data.filter(
      (item) => Number(item.cycleno) === maxCycleMap[item.binname],
    );
  };

  const filteredData = getMaxCycleData(drierStatusReportEven?.data || []);

  const getMaxCycleDataOdd = (data) => {
    const maxCycleMap = {};
    data.forEach((item) => {
      const bin = item.Binname;
      const cycle = Number(item.cycleno) || 0;
      if (!maxCycleMap[bin] || cycle > maxCycleMap[bin]) {
        maxCycleMap[bin] = cycle;
      }
    });
    return data.filter(
      (item) => Number(item.cycleno) === maxCycleMap[item.Binname],
    );
  };

  const filteredDataOdd = getMaxCycleDataOdd(drierStatusReportOdd?.data || []);

  const getQAFlagTextColor = (value) => {
    const colors = {
      GF: "#43780a",
      YF: "#FCEE4E",
      RF: "#FF7070",
      OF: "#893805",
    };
    return colors[value] || "inherit";
  };

  const getDocumentStatusBackground = (value) => {
    const colors = {
      "Airflow UP": "#A9D18E",
      "Airflow Down": "#FFC000",
      "Filling Stop": "#FFE699",
      "Filling Start": "#96FACA",
      "Drying Stop": "#00B0F0",
      Shelling: "#0070C0",
      Running: "#AEFA7F",
      Empty: "#AEFA7F",
    };
    return colors[value] || "transparent";
  };

  const usableBinCapacity = calculateUsableBinCapacity();
  const scrollingText = ` ${params?.note || ""}`;

  // Inject responsive styles
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      /* Base responsive styles */
      * {
        box-sizing: border-box;
      }
      
      .container {
        max-width: 100%;
        width: 100%;
        overflow-x: hidden;
        padding: 0 16px;
      }
 


      @media (max-width: 768px) {
        .container {
          padding: 0 12px;
        }
      }
      
      @media (max-width: 480px) {
        .container {
          padding: 0 8px;
        }
      }
      
      /* Table layouts - NO SCROLLING ON LARGE SCREENS */
      .table-layout {
        width: 100%;
        overflow-x: visible !important;
      }
      
      .table-column {
        width: 100%;
        overflow-x: visible !important;
      }
      
      .horizontal-table {
        min-width: auto !important;
        width: 100%;
      }
      
      /* Only allow scrolling on tablet and mobile when needed */
      @media (max-width: 1024px) {
        .table-layout {
          overflow-x: auto !important;
        }
        
        .table-column {
          overflow-x: auto !important;
        }
        
        .horizontal-table {
          min-width: 600px !important;
        }
      }
      
      /* Header responsive */
      @media (max-width: 1024px) {
        .header {
          flex-wrap: wrap !important;
          justify-content: center !important;
          gap: 12px !important;
          padding: 10px !important;
        }
        
        .header h1 {
          order: 3 !important;
          width: 100% !important;
          text-align: center !important;
          margin: 5px 0 !important;
        }
        
        .home-button {
          order: 1 !important;
        }
        
        .controls {
          order: 2 !important;
        }
        
        .header-info {
          order: 4 !important;
          width: 100% !important;
          display: flex !important;
          justify-content: center !important;
          margin: 5px 0 !important;
        }
        
        .date-display {
          order: 5 !important;
        }
        
        .logo-container {
          order: 6 !important;
        }
      }
      
      /* Table row responsive */
      @media (max-width: 768px) {
        .table-row > div[style*="display: flex"] {
          flex-direction: column !important;
        }
        
        .table-row > div[style*="display: flex"] > div {
          width: 100% !important;
          margin-bottom: 16px !important;
        }
      }
      
   
      
      /* No data image responsive */
      .no-data-image {
        max-width: 150px;
        width: 100%;
        height: auto;
      }
      
      @media (max-width: 767px) {
        .no-data-image {
          max-width: 100px;
        }
        
        .no-data p {
          font-size: 12px;
          text-align: center;
          padding: 0 16px;
        }
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <div
      className="container"
      style={{ position: "relative", minHeight: "100vh" }}
    >
      <header
        className="header"
        style={{
          flexWrap: "wrap",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <button
          className="home-button"
          onClick={() =>
            navigate("/menu", {
              state: { hideModal: true },
            })
          }
          style={{ background: "none", border: "none", cursor: "pointer" }}
        >
          <Home
            className="home-icon"
            size={120}
            style={{
              width: "clamp(24px, 5vw, 35px)",
              height: "clamp(24px, 5vw, 40px)",
            }}
          />
        </button>
        <div
          className="controls"
          style={{ display: "flex", gap: "2px", alignItems: "center" }}
        >
          <ChevronLeftCircle
            size={40}
            onClick={onPrevious}
            style={{
              cursor: "pointer",
              width: "clamp(28px, 6vw, 40px)",
              height: "clamp(28px, 6vw, 40px)",
            }}
          />
          {isPaused ? (
            <PlayCircle
              size={40}
              onClick={() => onPause(false)}
              style={{
                cursor: "pointer",
                width: "clamp(28px, 6vw, 40px)",
                height: "clamp(28px, 6vw, 40px)",
              }}
            />
          ) : (
            <PauseCircle
              size={40}
              onClick={() => onPause(true)}
              style={{
                cursor: "pointer",
                width: "clamp(28px, 6vw, 40px)",
                height: "clamp(28px, 6vw, 40px)",
              }}
            />
          )}
          <ChevronRightCircle
            size={40}
            onClick={onNext}
            style={{
              cursor: "pointer",
              width: "clamp(28px, 6vw, 40px)",
              height: "clamp(28px, 6vw, 40px)",
            }}
          />
        </div>
        <h1
          style={{
            fontSize: "clamp(16px, 4vw, 28px)",
            margin: "5px 0",
            textAlign: "center",
          }}
        >
          Drier Status Report
        </h1>
        {!loading && drierStatusReport?.data?.length > 0 && (
          <div className="header-info">
            <span
              style={{
                color: "#FFEB3B",
                fontSize: "clamp(14px, 3vw, 28px)",
                fontWeight: "bold",
              }}
            >
              <span
                style={{
                  background: "linear-gradient(90deg, #0A2A88, #1565C0)",
                  color: "#FFFFFF",
                  padding: "6px 12px",
                  borderRadius: "30px",
                  fontSize: "clamp(12px, 2.5vw, 28px)",
                  fontWeight: "800",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                  border: "2px solid rgba(255,255,255,0.7)",
                  letterSpacing: "0.5px",
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    color: "#BBDEFB",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    opacity: 0.9,
                  }}
                >
                  Organization
                </span>
                <span
                  style={{
                    width: "2px",
                    height: "clamp(16px, 4vw, 22px)",
                    background: "rgba(255,255,255,0.5)",
                  }}
                />
                <span
                  style={{
                    color: "#FFFFFF",
                    wordBreak: "break-word",
                    maxWidth: "clamp(150px, 40vw, 400px)",
                    fontWeight: "900",
                    whiteSpace: "normal",
                    textAlign: "center",
                  }}
                >
                  {drierStatusReport?.data[0]?.orgname || "-"}
                </span>
              </span>
            </span>
          </div>
        )}
        <div
          className="date-display"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: "clamp(11px, 3vw, 14px)" }}>
            Current Date:
          </span>
          <input
            type="text"
            value={currentDate}
            readOnly
            className="current-date"
            style={{
              width: "clamp(70px, 15vw, 80px)",
              minWidth: "60px",
              padding: "4px 8px",
              fontSize: "clamp(10px, 2.5vw, 14px)",
              border: "1px solid #ccc",
              borderRadius: "4px",
              textAlign: "center",
            }}
          />
        </div>
        <div className="logo-container">
          <a
            href="https://www.digiopenservices.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/Logo.png"
              alt="Logo"
              className="header-logo"
              style={{
                maxWidth: "clamp(80px, 15vw, 110px)",
                height: "clamp(30px, 6vw, 40px)",
                objectFit: "contain",
                borderRadius: "6px",
                margin: "2px 10px",
                cursor: "pointer",
              }}
            />
          </a>
        </div>
      </header>

      {loading && <p className="loading">Loading data, please wait...</p>}
      {error && <p className="error">{error}</p>}

      {!loading &&
        (!drierStatusReport?.data || drierStatusReport?.data?.length === 0) && (
          <div
            className="no-data"
            style={{ textAlign: "center", padding: "40px 20px" }}
          >
            <img
              src={noDataImage}
              alt="No data available"
              className="no-data-image"
            />
            <p>
              No data available for the selected Organization, Season and
              DrierType.
            </p>
          </div>
        )}

      <div>
        {/* Two Column Layout - Fully Responsive */}
        <div className="table-row">
          {!loading && drierStatusReport?.data?.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "stretch",
                gap: "12px",
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              {/* LEFT TABLE */}
              <div
                className="table-layout"
                style={{
                  flex: "1 1 40%",
                  minWidth: "280px",
                }}
              >
                <table
                  className="horizontal-table custom-font-table"
                  style={{
                    borderCollapse: "collapse",
                    width: "100%",
                    tableLayout: "auto",
                    border: "1px solid #ddd",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        colSpan={2}
                        style={{
                          padding: "10px",
                          backgroundColor: "#0070C0",
                          color: "white",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          fontSize: "clamp(12px, 3vw, 14px)",
                        }}
                      >
                        Season: {drierStatusReport.data[0]?.Season_Name || "-"}
                      </th>
                      <th
                        colSpan={2}
                        style={{
                          padding: "10px",
                          backgroundColor: "#0070C0",
                          color: "white",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          fontSize: "clamp(12px, 3vw, 14px)",
                        }}
                      >
                        Type of Dryer:{" "}
                        <span
                          style={{
                            color: "#FFEB3B",
                            fontWeight: "bold",
                            fontSize: "clamp(13px, 3.5vw, 16px)",
                            textShadow: "0 0 2px black",
                          }}
                        >
                          {drierStatusReport.data[0]?.Typeof_Dryer || "-"}
                        </span>
                      </th>
                      <th
                        colSpan={2}
                        style={{
                          padding: "10px",
                          backgroundColor: "#0070C0",
                          color: "white",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          fontSize: "clamp(12px, 3vw, 14px)",
                        }}
                      >
                        Reporting on: {currentDate || "-"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        style={{
                          padding: "8px",
                          fontWeight: "bold",
                          border: "1px solid #ddd",
                          backgroundColor: "#f9f9f9",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        No Of Bins Empty
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          backgroundColor: getDocumentStatusBackground("Empty"),
                          textAlign: "center",
                          border: "1px solid #ddd",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {drierStatusReport.data[0]?.totalnoofbeans -
                          drierStatusReport.data[0]?.noofbinsnonempty || "-"}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          fontWeight: "bold",
                          border: "1px solid #ddd",
                          backgroundColor: "#f9f9f9",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        No Of Bins Stop
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          backgroundColor:
                            getDocumentStatusBackground("Drying Stop"),
                          textAlign: "center",
                          border: "1px solid #ddd",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {drierStatusReport.data[0]?.noofbinsstop || "-"}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          fontWeight: "bold",
                          border: "1px solid #ddd",
                          backgroundColor: "#f9f9f9",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        No Of Bins Filing
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          backgroundColor:
                            getDocumentStatusBackground("Filling Start"),
                          textAlign: "center",
                          border: "1px solid #ddd",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {drierStatusReport.data[0]?.noofbinsfillingstart || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          padding: "8px",
                          fontWeight: "bold",
                          border: "1px solid #ddd",
                          backgroundColor: "#f9f9f9",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        No Of Bins Up
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          backgroundColor:
                            getDocumentStatusBackground("Airflow UP"),
                          textAlign: "center",
                          border: "1px solid #ddd",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {drierStatusReport.data[0]?.noofbinsup || "-"}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          fontWeight: "bold",
                          border: "1px solid #ddd",
                          backgroundColor: "#f9f9f9",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        No Of Bins Down
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          backgroundColor:
                            getDocumentStatusBackground("Airflow Down"),
                          textAlign: "center",
                          border: "1px solid #ddd",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {drierStatusReport.data[0]?.noofbinsdown || "-"}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          fontWeight: "bold",
                          border: "1px solid #ddd",
                          backgroundColor: "#f9f9f9",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        No Of Bins Shelling
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          backgroundColor:
                            getDocumentStatusBackground("Shelling"),
                          textAlign: "center",
                          border: "1px solid #ddd",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {drierStatusReport.data[0]?.noofbinsshelling || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          padding: "10px",
                          fontWeight: "bold",
                          textAlign: "center",
                          backgroundColor: "#0070C0",
                          color: "white",
                          border: "1px solid #ddd",
                          fontSize: "clamp(12px, 3vw, 14px)",
                        }}
                      >
                        Web Cob Under Drying (Qty in Tons)
                      </td>
                      <td
                        colSpan={2}
                        style={{
                          padding: "10px",
                          fontWeight: "bold",
                          fontSize: "clamp(14px, 4vw, 18px)",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          color: "#856c1f",
                          backgroundColor: "#fff8e1",
                        }}
                      >
                        {drierStatusReport.data[0]?.TotalQty
                          ? (drierStatusReport.data[0].TotalQty / 1000).toFixed(
                              2,
                            )
                          : "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* RIGHT TABLE - NO SCROLLING ON LARGE SCREENS */}
              <div
                className="table-layout"
                style={{
                  flex: "1 1 50%",
                  minWidth: "280px",
                }}
              >
                <table
                  className="horizontal-table custom-font-table"
                  style={{
                    borderCollapse: "collapse",
                    width: "100%",
                    tableLayout: "auto",
                    border: "1px solid #ddd",
                  }}
                >
                  <tbody>
                    <tr>
                      <th
                        colSpan={2}
                        style={{
                          padding: "20px",
                          border: "none",
                          visibility: "hidden",
                        }}
                      />
                      <th
                        colSpan={2}
                        style={{
                          visibility: "hidden",
                          border: "none",
                        }}
                      >
                        <span
                          style={{
                            color: "#FFEB3B",
                            fontWeight: "bold",
                            fontSize: "16px",
                            textShadow: "0 0 2px black",
                          }}
                        />
                      </th>
                      <th
                        colSpan={2}
                        style={{
                          padding: "10px",
                          backgroundColor: "#0070C0",
                          color: "white",
                          textAlign: "center",
                          border: "none",
                          fontSize: "14px",
                          visibility: "hidden",
                        }}
                      />
                      <th
                        colSpan={2}
                        style={{
                          padding: "10px",
                          backgroundColor: "#ffffff",
                          color: "white",
                          textAlign: "center",
                          border: "none",
                          fontSize: "14px",
                          visibility: "hidden",
                        }}
                      />
                    </tr>
                    <tr>
                      <td
                        style={{
                          padding: "6px 8px",
                          fontWeight: "bold",
                          textAlign: "left",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        UP_FRONT_TEMP:
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          backgroundColor: "#726f6f1c",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {DryingTransactionTemp?.[0]?.upfronttemp || "-"}
                      </td>
                      <td
                        style={{
                          paddingLeft: "clamp(10px, 2vw, 20px)",
                          padding: "6px 8px",
                          fontWeight: "bold",
                          textAlign: "left",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        UP_DP(inWC):
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          backgroundColor: "#726f6f1c",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {DryingTransactionTemp?.[0]?.updpinwc || "-"}
                      </td>
                      <td
                        style={{
                          paddingLeft: "clamp(10px, 2vw, 20px)",
                          padding: "6px 8px",
                          fontWeight: "bold",
                          textAlign: "left",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        UP_HUMIDITY (%RH):
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          backgroundColor: "#726f6f1c",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {DryingTransactionTemp?.[0]?.uphumidityrh || "-"}
                      </td>
                      <td
                        style={{
                          paddingLeft: "clamp(10px, 2vw, 20px)",
                          padding: "6px 8px",
                          fontWeight: "bold",
                          textAlign: "left",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        UP_REAR_TEMP:
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          backgroundColor: "#726f6f1c",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {DryingTransactionTemp?.[0]?.upreartemp || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          padding: "6px 8px",
                          fontWeight: "bold",
                          textAlign: "left",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        LP_FRONT_TEMP:
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          backgroundColor: "#726f6f1c",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {DryingTransactionTemp?.[0]?.lpfronttemp || "-"}
                      </td>
                      <td
                        style={{
                          paddingLeft: "clamp(10px, 2vw, 20px)",
                          padding: "6px 8px",
                          fontWeight: "bold",
                          textAlign: "left",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        LP_DP(inWC):
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          backgroundColor: "#726f6f1c",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {DryingTransactionTemp?.[0]?.lpdpinwc || "-"}
                      </td>
                      <td
                        style={{
                          paddingLeft: "clamp(10px, 2vw, 20px)",
                          padding: "6px 8px",
                          fontWeight: "bold",
                          textAlign: "left",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        LP_HUMIDITY (%RH):
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          backgroundColor: "#726f6f1c",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {DryingTransactionTemp?.[0]?.lphumidityrh || "-"}
                      </td>
                      <td
                        style={{
                          paddingLeft: "clamp(10px, 2vw, 20px)",
                          padding: "6px 8px",
                          fontWeight: "bold",
                          textAlign: "left",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        LP_REAR_TEMP:
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          backgroundColor: "#726f6f1c",
                          textAlign: "center",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          width: "6%",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        {DryingTransactionTemp?.[0]?.lpreartemp || "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Usable Empty Bin Capacity Table */}
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: "1px",
                    tableLayout: "auto",
                  }}
                >
                  <tbody>
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          padding: "12px 4px",
                          fontWeight: "bold",
                          textAlign: "center",
                          backgroundColor: "#70AD47",
                          color: "white",
                          border: "1px solid #ddd",
                          width: "25%",
                          whiteSpace: "nowrap",
                          fontSize: "clamp(11px, 2.5vw, 14px)",
                        }}
                      >
                        Usable Empty Bin Capacity @85%
                      </td>
                      <td
                        colSpan={2}
                        style={{
                          padding: "8px",
                          fontWeight: "bold",
                          textAlign: "center",
                          backgroundColor: "#E8F5E9",
                          border: "1px solid #ddd",
                          fontSize: "clamp(14px, 4vw, 18px)",
                          width: "6%",
                        }}
                      >
                        {usableBinCapacity} MT
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Data Tables */}
        <div className="table-layout">
          <div className="table-column">
            {!loading && filteredData.length > 0 && (
              <table
                className="horizontal-table custom-font-table"
                style={{
                  tableLayout: "auto",
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        width: "clamp(80px, 15vw, 105px)",
                        textAlign: "center",
                        padding: "clamp(3px, 1.5vw, 4px)",
                        border: "1px solid #ddd",
                        fontSize: "clamp(10px, 2.5vw, 14px)",
                      }}
                    >
                      Bin
                    </th>
                    {filteredData.map((item, index) => (
                      <th
                        key={index}
                        style={{
                          width: "clamp(60px, 10vw, 73px)",
                          textAlign: "center",
                          padding: "clamp(3px, 1.5vw, 4px)",
                          border: "1px solid #ddd",
                          fontSize: "clamp(10px, 2.5vw, 14px)",
                        }}
                      >
                        {item.binname || "-"}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(columnMappingTable2).map((key, rowIndex) => {
                    const isBinCapacityRow = key === "bincapacity";
                    return (
                      <tr key={rowIndex}>
                        <th
                          className="header-column"
                          style={{
                            width: "clamp(80px, 15vw, 100px)",
                            maxWidth: "clamp(80px, 15vw, 100px)",
                            textAlign: "left",
                            padding: "clamp(3px, 1.5vw, 4px)",
                            border: "1px solid #ddd",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            fontSize: "clamp(9px, 2vw, 13px)",
                          }}
                        >
                          {columnMappingTable2[key]}
                        </th>
                        {filteredData.map((item, colIndex) => {
                          const isEmptyStatus =
                            item.documentstatus?.trim().toLowerCase() ===
                            "empty";
                          const allowedKeys = [
                            "cycleno",
                            "documentstatus",
                            "fieldvariety",
                            "qaflag",
                          ];
                          const columnColor = getColumnColorForBin(item);
                          const isDocumentStatusColumn =
                            key === "documentstatus";
                          const isQAFlagColumn = key === "qaflag";
                          const isAirflowStatus =
                            item.documentstatus === "Airflow UP" ||
                            item.documentstatus === "Airflow Down";

                          let backgroundColor = "transparent";

                          if (isDocumentStatusColumn) {
                            backgroundColor = getDocumentStatusBackground(
                              item.documentstatus,
                            );
                          } else if (
                            columnColor !== "transparent" &&
                            !isEmptyStatus
                          ) {
                            backgroundColor = columnColor;
                          } else if (isAirflowStatus && !isQAFlagColumn) {
                            backgroundColor = getDocumentStatusBackground(
                              item.documentstatus,
                            );
                          }

                          let displayValue = item[key] || "-";
                          if (isBinCapacityRow && !isEmptyStatus) {
                            const totalIntake = item.totallintakehandlingqty;
                            const capacityData = calculateBinCapacityPercentage(
                              item[key],
                              totalIntake,
                            );
                            displayValue = capacityData.display;
                          }

                          return (
                            <td
                              key={colIndex}
                              style={{
                                width: "clamp(60px, 10vw, 100px)",
                                textAlign: "center",
                                padding: "clamp(3px, 1.5vw, 4px)",
                                border: "1px solid #ddd",
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                                backgroundColor: backgroundColor,
                                color: isQAFlagColumn
                                  ? getQAFlagTextColor(item[key])
                                  : "inherit",
                                fontWeight: isQAFlagColumn ? "bold" : "normal",
                                fontSize: "clamp(9px, 2vw, 13px)",
                              }}
                            >
                              {isEmptyStatus && !allowedKeys.includes(key)
                                ? "-"
                                : displayValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Drier Status ODD Table */}
            {!loading && filteredDataOdd.length > 0 && (
              <table
                className="horizontal-table custom-font-table"
                style={{
                  tableLayout: "auto",
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        width: "clamp(80px, 15vw, 105px)",
                        maxWidth: "clamp(80px, 15vw, 105px)",
                        textAlign: "center",
                        padding: "clamp(3px, 1.5vw, 4px)",
                        border: "1px solid #ddd",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        fontSize: "clamp(10px, 2.5vw, 14px)",
                      }}
                    >
                      Bin
                    </th>
                    {filteredDataOdd.map((item, index) => (
                      <th
                        key={index}
                        style={{
                          width: "clamp(60px, 10vw, 73px)",
                          maxWidth: "clamp(60px, 10vw, 95px)",
                          textAlign: "center",
                          padding: "clamp(3px, 1.5vw, 4px)",
                          border: "1px solid #ddd",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                          fontSize: "clamp(10px, 2.5vw, 14px)",
                        }}
                      >
                        {item.Binname || "-"}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(columnMappingTable3).map((key, rowIndex) => {
                    const isBinCapacityRow = key === "bincapacity";
                    return (
                      <tr key={rowIndex}>
                        <th
                          className="header-column"
                          style={{
                            width: "clamp(80px, 15vw, 100px)",
                            maxWidth: "clamp(80px, 15vw, 100px)",
                            textAlign: "left",
                            padding: "clamp(3px, 1.5vw, 4px)",
                            border: "1px solid #ddd",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            fontSize: "clamp(9px, 2vw, 13px)",
                          }}
                        >
                          {columnMappingTable3[key]}
                        </th>
                        {filteredDataOdd.map((item, colIndex) => {
                          const isEmptyStatus =
                            item.documentstatus?.trim().toLowerCase() ===
                            "empty";
                          const allowedKeys = [
                            "cycleno",
                            "documentstatus",
                            "fieldvariety",
                            "qaflag",
                          ];
                          const columnColor = getColumnColorForBin(item);
                          const isDocumentStatusColumn =
                            key === "documentstatus";
                          const isQAFlagColumn = key === "qaflag";
                          const isAirflowStatus =
                            item.documentstatus === "Airflow UP" ||
                            item.documentstatus === "Airflow Down";

                          let backgroundColor = "transparent";

                          if (isDocumentStatusColumn) {
                            backgroundColor = getDocumentStatusBackground(
                              item.documentstatus,
                            );
                          } else if (
                            columnColor !== "transparent" &&
                            !isEmptyStatus
                          ) {
                            backgroundColor = columnColor;
                          } else if (isAirflowStatus && !isQAFlagColumn) {
                            backgroundColor = getDocumentStatusBackground(
                              item.documentstatus,
                            );
                          }

                          let displayValue = item[key] || "-";
                          if (isBinCapacityRow && !isEmptyStatus) {
                            const totalIntake = item.totallintakehandlingqty;
                            const capacityData = calculateBinCapacityPercentage(
                              item[key],
                              totalIntake,
                            );
                            displayValue = capacityData.display;
                          }

                          return (
                            <td
                              key={colIndex}
                              style={{
                                width: "clamp(60px, 10vw, 100px)",
                                textAlign: "center",
                                padding: "clamp(3px, 1.5vw, 4px)",
                                border: "1px solid #ddd",
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                                backgroundColor: backgroundColor,
                                color: isQAFlagColumn
                                  ? getQAFlagTextColor(item[key])
                                  : "inherit",
                                fontWeight: isQAFlagColumn ? "bold" : "normal",
                                fontSize: "clamp(9px, 2vw, 13px)",
                              }}
                            >
                              {isEmptyStatus && !allowedKeys.includes(key)
                                ? "-"
                                : displayValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      <div
        style={{
          position: "fixed",
          bottom: "40px",
          left: 0,
          right: 0,
          color: "#022fe3",
          padding: "12px 0",
          overflow: "hidden",
          whiteSpace: "nowrap",
          fontFamily: "monospace",
          fontSize: "25px",
          fontWeight: "700",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            display: "inline-block",
            animation: "scrollRightToLeft 35s linear infinite",
            whiteSpace: "nowrap",
            paddingLeft: "100%",
          }}
        >
          <span>{scrollingText}</span>
        </div>

        <style>
          {`
      @keyframes scrollRightToLeft {
        0% {
          transform: translateX(0%);
        }
        100% {
          transform: translateX(-100%);
        }
      }
    `}
        </style>
      </div>
    </div>
  );
};

export default DrierStatusReport;
